import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
   Stack,
} from "@mui/material";
import StyledTooltip from "../../utils/styledToolTip";

export default function PhysicalActivity({projects}) {

  // Paleta focada em 3 projetos
  const palette = ["#7C3AED", "#22C55E", "#3B82F6"]; // roxo, verde, azul
  const colorForIndex = (i) => palette[i % palette.length];

  //console.log("Projects=", projects);
  
  // valor máximo para a escala comum
  const maxValue = Math.max(
    ...projects.map(p =>
      (p.projectInfo.averageMinutesOfActivityPerUserPerWeek ?? 0)
    )
  );

  const percentage = (value) => {
    const ret = maxValue > 0 ? (value / maxValue) * 100 : 0;
    return ret;
  }

  const increasePhysicalActivity = (info) => {
    const value = (info.physicalActivityAsPercentOfTheTarget / 100) * info.averageMinutesOfActivityPerUserPerWeek;
    return value.toFixed(2)
  }

  const roundToTwo = (value) => {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box display="flex" alignItems="center">
        <Typography variant="h6">Physical Activity per person</Typography>
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ ml: 1, cursor: "help" }}
        >  
          <StyledTooltip title="The calculation is based on the assumption that individuals will undertake two journeys per day, five days per week, and for a total of 25 weeks per year. This assumption takes into consideration the typical commuting patterns of an average individual while also accounting for factors that may affect the likelihood of walking or cycling, such as holidays and weather conditions."
           arrow><span className="icon"></span></StyledTooltip>
        </Typography>
      </Box>

      <TableContainer sx={{ width: '100%' }}>
        <Table stickyHeader sx={{ mt: 1.5, tableLayout: "fixed", width: '100%' }}>
          <TableHead>
            <TableRow
              sx={{
                position: "relative",
                "& > .MuiTableCell-root": {
                  borderBottom: "1px solid #e0e0e0 !important",
                  borderCollapse: "collapse",
                },
              }}>
              <TableCell sx={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    align="left"
                    sx={{ fontWeight: 600, px: 0 }}>
                  Metric
                </Typography>
              </TableCell>

              {/* Gera uma coluna por projeto */}
              {projects.map((project, idx) => (
                <TableCell
                  key={project.id ?? project.projectName}
                  align="right"
                  sx={{ width: 160, maxWidth: 160, px: 0 }}>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    align="center"
                    sx={{ fontWeight: 600, px: 1 }}>
                    {project.projectName}
                  </Typography>
                </TableCell>
              ))}
              <TableCell sx={{ width: 80, maxWidth: 80 }}>
                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    align="center"
                    sx={{ fontWeight: 600, px: 1 }}>
                  Unit
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>

            <TableRow aria-label={"details"}>
              <TableCell
                sx={{
                  width: "calc(100% - " + projects.length * 160 + "px )",
                  py: 1.25,
                  borderBottom: "1px solid #e0e0e0 !important",
                }}>
                <Box sx={{ 
                  display: "flex", alignItems: "center", gap: 1
                  }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, width: '280px' }}>
                    Increase in Physical Activity<br/>(walking & cycling)
                  </Typography>
                </Box>
              </TableCell>

              {/* Colunas por projeto */}
              {projects.map((project) => (
                <TableCell
                  key={project.id ?? project.projectName}
                  align="right"
                  sx={{
                    width: 160,
                    minWidth: 160,
                    maxWidth: 160,
                    borderBottom: "1px solid #e0e0e0 !important"
                  }}>
                  <Typography
                    variant="body2"
                    align="center"
                    noWrap
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                    title={String("")}>  
                      { 
                        project.projectInfo.averageMinutesOfActivityPerUserPerWeek != null
                          ? roundToTwo(project.projectInfo.averageMinutesOfActivityPerUserPerWeek)
                          : "--"
                      }
                  </Typography>
                </TableCell>
              ))}

              <TableCell 
                sx={{ width: 70, maxWidth: 70,  px:0, borderBottom: "1px solid #e0e0e0 !important" }}>
                  <Typography
                    variant="body2"
                    align="center"
                    noWrap
                   >
                    mins/week
                  </Typography>
                </TableCell>
            </TableRow>

          </TableBody>

        </Table>
      </TableContainer>
      
      <Box spacing={1} sx={{mt: 6}}>

        <Typography
          variant="h6"
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
          Increase in physical activity (walking & cycling)
        </Typography>

        {projects.map((project, idx) => (
          <Stack key={project.projectName} spacing={0}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              <Typography
                variant="subtitle1"
                sx={{ color:  colorForIndex(idx), fontWeight: 500, lineHeight: 1, pt: 1.5 }}
              >
                {project.projectName}
              </Typography>

            </Stack>

            {/* Barra */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: '100%' }}
            >

              <Box
                sx={{
                  height: '14px',
                  width: `${percentage(project.projectInfo.averageMinutesOfActivityPerUserPerWeek)}%`,
                  backgroundColor: `${colorForIndex(idx)}50`, 
                  borderRadius: 0,
                  transition: 'width 0.4s ease',
                }}
              />
       
              <Typography
                variant="body2"
                sx={{
                  color: colorForIndex(idx),
                  fontWeight: 500,
                  minWidth: 120, // 👈 largura fixa para alinhar tudo
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                }}
              >
                {/*increasePhysicalActivity(project.projectInfo)} mins/week*/
                  roundToTwo(project.projectInfo.averageMinutesOfActivityPerUserPerWeek)} mins/week
              </Typography>

            </Stack>
          </Stack>         
        ))}
      </Box>

    </Box>
  );
}
